import type {
  HilPresentationRequestEnvelope,
  HilPresentationAssignee,
  HilPresentation,
  HilPresentationAction,
} from '@bizfirst/hil-types';
import { HilPresentationType } from '@bizfirst/hil-types';
import { HilStepReader } from './HilStepReader';

export class HilReaderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HilReaderError';
  }
}

export interface HilReaderOptions {
  currentUserID?: string;
}

export class HilPresentationRequestEnvelopeReader {
  private readonly env: HilPresentationRequestEnvelope;
  private readonly currentUserID: string | undefined;
  private readonly sortedPresentations: HilPresentation[];

  constructor(raw: HilPresentationRequestEnvelope, options?: HilReaderOptions) {
    if (!raw || typeof raw !== 'object') {
      throw new HilReaderError('Cannot construct reader from invalid envelope');
    }
    this.env = raw;
    this.currentUserID = options?.currentUserID;
    this.sortedPresentations = [...(raw.Presentations ?? [])].sort((a, b) => a.Order - b.Order);
  }

  // ── Validity ─────────────────────────────────────────────────────────────────
  isValid(): boolean {
    return true;
  }

  isHilMessage(): boolean {
    return (this.env.Assignees?.length ?? 0) > 0 && (this.env.Presentations?.length ?? 0) > 0;
  }

  // ── Session ──────────────────────────────────────────────────────────────────
  getEngageSessionID(): number {
    return this.env.Session?.EngageSessionID ?? 0;
  }

  getResumeCallbackURL(): string {
    return this.env.Session?.ResumeCallbackURL ?? '';
  }

  getCorrelationID(): string {
    return this.env.Session?.CorrelationID ?? '';
  }

  getNodeKey(): string | null {
    return this.env.Session?.NodeKey ?? null;
  }

  getProcessElementID(): string | null {
    return this.env.Session?.ProcessElementID ?? null;
  }

  // ── Assignees ─────────────────────────────────────────────────────────────────
  firstActor(): HilPresentationAssignee | null {
    return this.env.Assignees?.[0] ?? null;
  }

  firstActorID(): string | null {
    return this.env.Assignees?.[0]?.ActorID ?? null;
  }

  firstAssignedTo(): string | null {
    return this.firstActorID();
  }

  isAssignedToMe(): boolean {
    if (!this.currentUserID) return false;
    return this.getAssignees().some(a => a.ActorID === this.currentUserID);
  }

  isAssignedTo(actorID: string): boolean {
    return this.getAssignees().some(a => a.ActorID === actorID);
  }

  getAssignees(): HilPresentationAssignee[] {
    return this.env.Assignees ?? [];
  }

  // ── HIL type & capability flags ───────────────────────────────────────────────
  hilType(): string {
    return this.env.HilType ?? '';
  }

  isFormSupported(): boolean {
    return this.sortedPresentations.some(p => p.PresentationType === HilPresentationType.Form);
  }

  isChatSupported(): boolean {
    return this.sortedPresentations.some(p => p.PresentationType === HilPresentationType.Chat);
  }

  isApprovalSupported(): boolean {
    return this.sortedPresentations.some(p => p.PresentationType === HilPresentationType.Approval);
  }

  isAtlasFormApplicable(): boolean {
    if (this.firstActor() === null) return false;
    if (!this.isFormSupported()) return false;
    if (this.firstAssignedTo() === null) return false;
    return true;
  }

  // ── Presentations ─────────────────────────────────────────────────────────────
  getPresentations(): HilPresentation[] {
    return this.sortedPresentations;
  }

  getPresentationCount(): number {
    return this.sortedPresentations.length;
  }

  isMultiStep(): boolean {
    return this.getPresentationCount() > 1;
  }

  getPresentation(stepOrder: number): HilPresentation | null {
    return this.sortedPresentations.find(p => p.Order === stepOrder) ?? null;
  }

  firstPresentation(): HilPresentation | null {
    return this.sortedPresentations[0] ?? null;
  }

  getFormPresentation(): HilPresentation | null {
    return this.sortedPresentations.find(p => p.PresentationType === HilPresentationType.Form) ?? null;
  }

  getChatPresentation(): HilPresentation | null {
    return this.sortedPresentations.find(p => p.PresentationType === HilPresentationType.Chat) ?? null;
  }

  getApprovalPresentation(): HilPresentation | null {
    return this.sortedPresentations.find(p => p.PresentationType === HilPresentationType.Approval) ?? null;
  }

  // ── Step-scoped reader ────────────────────────────────────────────────────────
  forStep(stepOrder: number): HilStepReader | null {
    const step = this.getPresentation(stepOrder);
    if (!step) return null;
    return new HilStepReader(step, this.env.Actions ?? []);
  }

  // ── Actions ───────────────────────────────────────────────────────────────────
  getActions(stepOrder?: number): HilPresentationAction[] {
    if (stepOrder !== undefined) {
      const step = this.getPresentation(stepOrder);
      if (step?.Actions && step.Actions.length > 0) return step.Actions;
    }
    return this.env.Actions ?? [];
  }

  getPrimaryAction(stepOrder?: number): HilPresentationAction | null {
    const actions = this.getActions(stepOrder);
    return actions.find(a => a.IsPrimary) ?? actions[0] ?? null;
  }

  getActionByKey(actionKey: string, stepOrder?: number): HilPresentationAction | null {
    return this.getActions(stepOrder).find(a => a.ActionKey === actionKey) ?? null;
  }

  // ── Form helpers ──────────────────────────────────────────────────────────────
  getFormID(stepOrder?: number): number | null {
    const step = stepOrder !== undefined ? this.getPresentation(stepOrder) : this.getFormPresentation();
    return step?.FormID ?? null;
  }

  getFormSchemaJson(stepOrder?: number): string | null {
    const step = stepOrder !== undefined ? this.getPresentation(stepOrder) : this.getFormPresentation();
    return step?.FormSchemaJson ?? null;
  }

  getPrePopulate(stepOrder?: number): Record<string, unknown> {
    const step = stepOrder !== undefined ? this.getPresentation(stepOrder) : this.getFormPresentation();
    return step?.PrePopulate ?? {};
  }

  getPromptMessage(stepOrder?: number): string | null {
    const step = stepOrder !== undefined ? this.getPresentation(stepOrder) : this.firstPresentation();
    return step?.PromptMessage ?? null;
  }

  getSummaryText(stepOrder?: number): string | null {
    const step = stepOrder !== undefined ? this.getPresentation(stepOrder) : this.firstPresentation();
    return step?.SummaryText ?? null;
  }

  // ── Display ───────────────────────────────────────────────────────────────────
  title(): string | null {
    return this.env.Title ?? null;
  }

  description(): string | null {
    return this.env.Description ?? null;
  }

  hasDeadline(): boolean {
    return !!this.env.Deadline;
  }

  deadline(): string | null {
    return this.env.Deadline ?? null;
  }

  // ── Custom data ───────────────────────────────────────────────────────────────
  customData(): Record<string, unknown> {
    return this.env.CustomData ?? {};
  }

  stepCustomData(stepOrder: number): Record<string, unknown> {
    return this.getPresentation(stepOrder)?.CustomData ?? {};
  }

  // ── Raw access (escape hatch) ─────────────────────────────────────────────────
  raw(): HilPresentationRequestEnvelope {
    return this.env;
  }
}
